import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { TrendingUp, Mail, Lock, ArrowRight, Chrome, KeyRound } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1);
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      if (user.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error("Please enter your email");
      return;
    }
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("If the email exists, a reset code has been sent");
      setResetStep(2);
    } catch (error) {
      toast.error("Failed to send reset code");
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || !newPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetCode,
        new_password: newPassword
      });
      toast.success("Password reset successfully! Please login.");
      setForgotDialogOpen(false);
      setResetStep(1);
      setForgotEmail("");
      setResetCode("");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-50 flex items-center justify-center p-6">
      {/* Back to Home */}
      <Link
        to="/"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-slate-600 hover:text-platinum transition-colors"
      >
        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-white" />
        </div>
        <span className="font-heading font-semibold">AndInvestTrading</span>
      </Link>

      <Card className="w-full max-w-md bg-white shadow-luxury border-slate-200">
        <CardHeader className="text-center pb-2">
          <CardTitle className="font-heading text-3xl text-platinum">Welcome Back</CardTitle>
          <CardDescription className="text-slate-500">
            Sign in to access your investment dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 gap-3 py-6"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
          >
            <Chrome className="w-5 h-5" />
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-slate-400">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">
                  Password
                </Label>
                <Dialog open={forgotDialogOpen} onOpenChange={setForgotDialogOpen}>
                  <DialogTrigger asChild>
                    <button 
                      type="button" 
                      className="text-sm text-amber-600 hover:text-amber-700"
                      onClick={() => setResetStep(1)}
                    >
                      Forgot password?
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-slate-200">
                    <DialogHeader>
                      <DialogTitle className="text-platinum font-heading">
                        {resetStep === 1 ? "Reset Password" : "Enter Reset Code"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      {resetStep === 1 ? (
                        <>
                          <div className="space-y-2">
                            <Label className="text-slate-700">Email Address</Label>
                            <Input
                              type="email"
                              placeholder="Enter your email"
                              value={forgotEmail}
                              onChange={(e) => setForgotEmail(e.target.value)}
                              className="border-slate-300"
                            />
                          </div>
                          <Button onClick={handleForgotPassword} className="w-full btn-gold">
                            Send Reset Code
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label className="text-slate-700">Reset Code</Label>
                            <Input
                              type="text"
                              placeholder="Enter 6-digit code"
                              value={resetCode}
                              onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                              className="border-slate-300 font-mono text-center text-lg tracking-widest"
                              maxLength={6}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-700">New Password</Label>
                            <Input
                              type="password"
                              placeholder="Enter new password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="border-slate-300"
                            />
                          </div>
                          <Button onClick={handleResetPassword} className="w-full btn-gold">
                            Reset Password
                          </Button>
                          <button 
                            type="button"
                            className="w-full text-sm text-slate-500 hover:text-slate-700"
                            onClick={() => setResetStep(1)}
                          >
                            Didn't receive code? Try again
                          </button>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-gold py-6 text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-platinum border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-amber-600 hover:text-amber-700 font-medium"
              data-testid="register-link"
            >
              Create account
            </Link>
          </p>

        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
